import { useEffect, useState } from "react";
import {
  Building2,
  CheckCircle2,
  Landmark,
  Link2,
  RefreshCw,
  RefreshCcwDot,
  Search,
  Trash2,
  AlertTriangle,
  ArrowLeft,
} from "lucide-react";
import { Button } from "../ui/Button";
import { Badge } from "../ui/Badge";
import { fmtDate } from "../../lib/format";
import api from "../../lib/api";
import type { BankAccount, BankAccountStatus, GcInstitution, SyncResult } from "../../types/bank";
import { bankAccountStatusLabels } from "../../types/bank";

const statusBadge: Record<BankAccountStatus, string> = {
  ACTIVE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  CONSENT_EXPIRED: "bg-amber-50 text-amber-700 border-amber-200",
  REVOKED: "bg-slate-100 text-slate-500 border-slate-200",
};

/** Verbergt alles behalve de laatste 4 tekens van de IBAN (rekeningnummers
 *  staan op schermen van meerdere rollen; geen volledige nummers nodig). */
function maskIban(iban?: string | null): string {
  if (!iban) return "—";
  const clean = iban.replace(/\s+/g, "");
  if (clean.length <= 4) return iban;
  return `•••• ${clean.slice(-4)}`;
}

/** Gekoppelde bankrekeningen (GoCardless): lijst met sync-status + koppel-flow
 *  (bank zoeken → naar bank → terug met ?ref → /complete). */
export function BankAccountsPanel({
  returnReference,
  onReturnDone,
  onChanged,
}: {
  /** Reference (?ref=) uit de terugkeer-URL van de bank, of null. */
  returnReference: string | null;
  /** Roept de pagina aan om de ?ref-parameter uit de URL te wissen. */
  onReturnDone: () => void;
  /** Na sync/verwijderen/koppeling — pagina kan transacties herladen. */
  onChanged?: () => void;
}) {
  const [view, setView] = useState<"list" | "koppelen">("list");
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [institutions, setInstitutions] = useState<GcInstitution[] | null>(null);
  const [instQuery, setInstQuery] = useState("");
  const [selected, setSelected] = useState<GcInstitution | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [completing, setCompleting] = useState(false);
  const [toast, setToast] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);

  const flash = (kind: "ok" | "err", msg: string) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 6000);
  };

  const load = async () => {
    try {
      const { data } = await api.get("/api/bank-connections");
      setAccounts(data ?? []);
    } catch {
      // stil — lege lijst is ook een geldige staat
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Terugkeer van de bank: ?ref=<reference> → /complete → flash + herladen.
  useEffect(() => {
    if (!returnReference) return;
    setCompleting(true);
    api
      .post("/api/bank-connections/complete", { reference: returnReference })
      .then(({ data }) => {
        flash("ok", `Koppeling voltooid — ${(data ?? []).length} rekening(en) gekoppeld. Klik "Nu synchroniseren" voor de transacties.`);
        load();
        onChanged?.();
      })
      .catch((err) => {
        flash("err", err?.response?.data?.error || "Voltooien mislukt — koppel opnieuw.");
      })
      .finally(() => {
        setCompleting(false);
        onReturnDone();
      });
  }, [returnReference]);

  const loadInstitutions = async () => {
    setInstitutions(null);
    try {
      const { data } = await api.get("/api/bank-connections/institutions");
      setInstitutions(data ?? []);
    } catch (err: any) {
      flash("err", err?.response?.data?.error || "Banklijst ophalen mislukt.");
    }
  };

  const startConnect = (inst: GcInstitution) => {
    setSelected(inst);
    setConnecting(true);
    api
      .post("/api/bank-connections/requisitions", { institutionId: inst.id })
      .then(({ data }) => {
        if (!data?.link) throw new Error("Geen koppel-URL ontvangen");
        // Volledige navigatie naar de bank; terugkeer = /banktransacties?ref=
        window.location.href = data.link;
      })
      .catch((err) => {
        flash("err", err?.response?.data?.error || "Koppeling starten mislukt.");
        setConnecting(false);
      });
  };

  const sync = async (acct: BankAccount) => {
    setSyncingId(acct.id);
    try {
      const { data } = await api.post<SyncResult>(`/api/bank-connections/${acct.id}/sync`);
      flash(
        "ok",
        `Sync klaar: ${data.inserted} nieuwe transactie(s), ${data.duplicates} dubbel overgeslagen${data.autoMatched ? `, ${data.autoMatched} automatisch gekoppeld` : ""}.`,
      );
      load();
      onChanged?.();
    } catch (err: any) {
      flash("err", err?.response?.data?.error || "Synchroniseren mislukt.");
    } finally {
      setSyncingId(null);
    }
  };

  const remove = async (acct: BankAccount) => {
    if (!confirm(`${acct.institutionName} ontkoppelen? Bestaande transacties blijven in de boeken staan.`)) return;
    try {
      await api.delete(`/api/bank-connections/${acct.id}`);
      flash("ok", "Koppeling verwijderd.");
      load();
      onChanged?.();
    } catch (err: any) {
      flash("err", err?.response?.data?.error || "Verwijderen mislukt.");
    }
  };

  const filteredInstitutions = (institutions ?? []).filter((i) =>
    i.name.toLowerCase().includes(instQuery.toLowerCase()),
  );

  if (view === "koppelen") {
    return (
      <div className="space-y-4">
        {toast && <FlashBanner kind={toast.kind} msg={toast.msg} />}
        <div className="surface-card p-6 space-y-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => setView("list")}>
              <ArrowLeft size={16} /> Terug
            </Button>
            <h3 className="font-bold text-slate-800 flex items-center gap-2">
              <Building2 size={22} className="text-brand-500" /> Kies je bank
            </h3>
          </div>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={instQuery}
              onChange={(e) => setInstQuery(e.target.value)}
              placeholder="Zoek bank… (IN, ABN, Rabo, Bunq, Revolut…)"
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-200"
            />
          </div>
          {institutions == null ? (
            <p className="text-sm text-slate-400 py-6 text-center">Banken laden…</p>
          ) : filteredInstitutions.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">Geen bank gevonden voor "{instQuery}".</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[26rem] overflow-y-auto">
              {filteredInstitutions.map((inst) => (
                <button
                  key={inst.id}
                  onClick={() => startConnect(inst)}
                  disabled={connecting}
                  className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:border-brand-300 hover:bg-brand-50/50 text-left transition-all disabled:opacity-50"
                >
                  {inst.logo ? (
                    <img src={inst.logo} alt="" className="w-10 h-10 rounded-lg object-contain bg-white border border-slate-100" />
                  ) : (
                    <Landmark size={28} className="text-slate-300 shrink-0" />
                  )}
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-slate-700 truncate">{inst.name}</span>
                    <span className="block text-xs text-slate-400">
                      {inst.transactionTotalDays} dagen historiek · toestemming {inst.maxAccessValidForDays} dagen
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
          {connecting && selected && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              Doorverbinden naar {selected.name}… Je keert automatisch terug naar AIBoek na het toestemming geven.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {toast && <FlashBanner kind={toast.kind} msg={toast.msg} />}
      {completing && (
        <p className="text-sm text-brand-700 bg-brand-50 border border-brand-200 rounded-xl px-4 py-3">Bankkoppeling afronden…</p>
      )}

      <div className="surface-card p-6">
        <div className="flex items-center justify-between mb-4 gap-3">
          <h3 className="font-bold text-slate-800 flex items-center gap-2">
            <Landmark size={22} className="text-brand-500" /> Gekoppelde bankrekeningen ({accounts.length})
          </h3>
          <Button
            onClick={() => {
              setView("koppelen");
              loadInstitutions();
            }}
          >
            <Link2 size={18} /> Bank koppelen
          </Button>
        </div>

        {loading ? (
          <p className="text-sm text-slate-400 py-6 text-center">Laden…</p>
        ) : accounts.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-10 text-center">
            <Landmark size={40} className="text-slate-300" />
            <p className="text-slate-600 font-medium">Nog geen bankrekening gekoppeld.</p>
            <p className="text-sm text-slate-400">
              Koppel je bank en transacties worden elke nacht automatisch opgehaald — niet meer handmatig importeren.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {accounts.map((acct) => (
              <div key={acct.id} className="rounded-2xl border border-slate-200 p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800 truncate">{acct.institutionName}</p>
                    <p className="text-sm text-slate-500">
                      {maskIban(acct.iban)}
                      {acct.ownerName ? ` · ${acct.ownerName}` : ""}
                    </p>
                  </div>
                  <Badge className={statusBadge[acct.status]}>{bankAccountStatusLabels[acct.status]}</Badge>
                </div>

                <p className="text-xs text-slate-400">
                  {acct.lastSyncAt
                    ? `Laatste sync: ${fmtDate(acct.lastSyncAt)}${
                        acct.lastSyncResult
                          ? ` · ${acct.lastSyncResult.inserted} nieuw · ${acct.lastSyncResult.duplicates} dubbel${
                              acct.lastSyncResult.autoMatched ? ` · ${acct.lastSyncResult.autoMatched} gematcht` : ""
                            }`
                          : ""
                      }`
                    : "Nog niet gesynchroniseerd"}
                  {acct.lastSyncResult?.fetched != null && ` · ${acct.lastSyncResult.fetched} ontvangen`}
                </p>

                {acct.lastError && (
                  <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 flex items-start gap-2">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" /> {acct.lastError}
                  </p>
                )}

                <div className="flex items-center gap-2 flex-wrap">
                  <Button size="sm" onClick={() => sync(acct)} loading={syncingId === acct.id} disabled={acct.status !== "ACTIVE"}>
                    <RefreshCcwDot size={15} /> Nu synchroniseren
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setView("koppelen");
                      loadInstitutions();
                    }}
                  >
                    <RefreshCw size={15} /> Opnieuw koppelen
                  </Button>
                  <Button size="sm" variant="ghost" className="!text-rose-600" onClick={() => remove(acct)}>
                    <Trash2 size={15} /> Verwijderen
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="surface-card p-5">
        <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
          <CheckCircle2 size={18} className="text-emerald-500" /> Hoe werkt het?
        </h4>
        <ul className="text-sm text-slate-500 space-y-1.5 list-disc pl-5">
          <li>Kies je bank en geef toestemming in je bank-app (secure PSD2-verbinding — we kunnen alleen lezen).</li>
          <li>De toestemming geldt 90 dagen; daarna koppel je opnieuw (je krijgt vanzelf een melding).</li>
          <li>Elke nacht synchroniseren we automatisch; nieuwe transacties worden direct geautomatcht.</li>
          <li>CSV-import blijft gewoon werken — dubbels worden automatisch herkend.</li>
        </ul>
      </div>
    </div>
  );
}

function FlashBanner({ kind, msg }: { kind: "ok" | "err"; msg: string }) {
  const ok = kind === "ok";
  return (
    <p
      className={`text-sm rounded-xl px-4 py-3 border flex items-start gap-2 ${
        ok ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-rose-700 bg-rose-50 border-rose-200"
      }`}
    >
      {ok ? <CheckCircle2 size={16} className="mt-0.5 shrink-0" /> : <AlertTriangle size={16} className="mt-0.5 shrink-0" />}
      {msg}
    </p>
  );
}